/* eslint-disable no-custom-animation/no-custom-animation */
import {
	Box,
	Collapse,
	createStyles,
	getStylesRef,
	Group,
	List,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { useDidUpdate, useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { Prism } from '@mantine/prism';
import type { Message } from '@repo/api-codegen';
import { Button, Text, TextSkeleton } from '@repo/foundations';
import { buttonHoverTransition } from '@repo/theme/primitives';
import { IconChevronDown } from '@tabler/icons-react';
import { useMemo, useRef } from 'react';
import * as yup from 'yup';
import { apiClient } from '../../../../api/common/axios';
import { useFmtSql } from '../../../../api/hooks/query/useFormatterQuery';
import { initiateIntegrationOauth } from '../../../../features/IntegrationOauthConnect';
import { TOOLS_GROUP_LABELS, TOOLS_ICONS } from '../../constants';
import { AnimatedContent } from '../AnimatedContent';
import { ThinkingText } from '../ThinkingText';
import { detectOAuthError } from '../utils';
import { ReasoningCollapse } from './ReasoningCollapse';

// Yup schemas for tool argument validation
const RunSqlArgsSchema = yup.object({
	query: yup.string().required(),
	integration_id: yup.string().optional(),
});

const ExecuteSqlArgsSchema = yup.object({
	queries: yup.array().of(yup.string().required()).required(),
	integration_id: yup.string().optional(),
});

const CreateTodosArgsSchema = yup.object({
	todos: yup.string().required(),
});

const DocumentFindingsArgsSchema = yup.object({
	findings: yup.string().required(),
});

const TriggerAgentArgsSchema = yup.object({
	agent_name: yup.string().required(),
	prompt: yup.string().required(),
});

const CreateMetricsArgsSchema = yup
	.object({
		bi_metrics: yup.array().optional(),
		bi_metric_ids: yup.array().of(yup.string()).optional(),
	})
	.noUnknown(true); // Allow additional properties

const CreateDashboardArgsSchema = yup
	.object({
		bi_metrics: yup.array().optional(),
		bi_metric_ids: yup.array().of(yup.string()).optional(),
	})
	.noUnknown(true); // Allow additional properties

// Type definitions for validated args
type RunSqlArgs = yup.InferType<typeof RunSqlArgsSchema>;
type ExecuteSqlArgs = yup.InferType<typeof ExecuteSqlArgsSchema>;
type CreateTodosArgs = yup.InferType<typeof CreateTodosArgsSchema>;
type DocumentFindingsArgs = yup.InferType<typeof DocumentFindingsArgsSchema>;
type TriggerAgentArgs = yup.InferType<typeof TriggerAgentArgsSchema>;
type CreateMetricsArgs = yup.InferType<typeof CreateMetricsArgsSchema>;
type CreateDashboardArgs = yup.InferType<typeof CreateDashboardArgsSchema>;

// Union type for all possible tool args
type ValidatedToolArgs =
	| RunSqlArgs
	| ExecuteSqlArgs
	| CreateTodosArgs
	| DocumentFindingsArgs
	| TriggerAgentArgs
	| CreateMetricsArgs
	| CreateDashboardArgs;

// Schema mapping for different tools
const TOOL_SCHEMAS = {
	run_sql: RunSqlArgsSchema,
	execute_sql: ExecuteSqlArgsSchema,
	create_todos: CreateTodosArgsSchema,
	document_findings: DocumentFindingsArgsSchema,
	trigger_agent: TriggerAgentArgsSchema,
	createMetrics: CreateMetricsArgsSchema,
	createDashboard: CreateDashboardArgsSchema,
} as const;

// Validation function with fallback
function validateToolArgs(
	toolName: string,
	args: Record<string, unknown>
): {
	validated: ValidatedToolArgs | null;
	isValid: boolean;
	error?: string;
} {
	const schema = TOOL_SCHEMAS[toolName as keyof typeof TOOL_SCHEMAS];
	if (!schema) {
		// No schema defined for this tool, return original args
		return {
			validated: args as ValidatedToolArgs,
			isValid: true,
		};
	}

	try {
		const validated = schema.validateSync(args, { abortEarly: false });
		return {
			validated,
			isValid: true,
		};
	} catch (error) {
		if (error instanceof yup.ValidationError) {
			return {
				validated: null,
				isValid: false,
				error: `Validation failed: ${error.errors.join(', ')}`,
			};
		}
		return {
			validated: null,
			isValid: false,
			error: 'Unknown validation error',
		};
	}
}

const useStyles = createStyles((theme) => ({
	indentedContent: {
		paddingLeft: theme.other.space[7],
	},
	toggleButton: {
		...buttonHoverTransition('color'),

		'&:hover': {
			[`.${getStylesRef('thinkingText')}`]: {
				color: theme.other.getColor('text/primary/default'),
				background: 'none',
				textFillColor: 'inherit',
			},

			[`.${getStylesRef('chevron')}`]: {
				color: theme.other.getColor('icon/primary/default'),
			},
		},

		'&[data-active]': {
			[`.${getStylesRef('thinkingText')}`]: {
				color: theme.other.getColor('text/primary/default'),
			},

			[`.${getStylesRef('chevron')}`]: {
				color: theme.other.getColor('icon/primary/default'),
			},
		},
	},
	thinkingText: {
		ref: getStylesRef('thinkingText'),
		color: theme.other.getColor('text/secondary/default'),
		...buttonHoverTransition('color'),
		maxWidth: '50%',
	},
	expandContent: {
		borderRadius: theme.radius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		backgroundColor: theme.other.getColor('surface/app/default'),
	},
	chevron: {
		ref: getStylesRef('chevron'),
		transition: 'transform 200ms ease',
		transform: 'rotate(-90deg)',
		color: theme.other.getColor('icon/secondary/default'),

		'&[data-expanded]': {
			transform: 'rotate(0deg)',
		},
	},
	sqlEditor: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.sm,
	},
	sqlCode: {
		backgroundColor: 'transparent !important',
	},
	preElement: {
		fontSize: theme.other.typography.text.xs,
		fontFamily: 'monospace',
		whiteSpace: 'pre-wrap',
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		color: theme.other.getColor('text/secondary/default'),
		padding: theme.other.space[2],
		borderRadius: theme.radius.xs,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		overflow: 'auto',
	},
	textLineHeight: {
		lineHeight: 1.4,
	},
	boldText: {
		fontWeight: theme.other.typography.weight.bold,
	},
	successText: {
		color: theme.other.getColor('text/success/default'),
	},
}));

interface ToolTimelineItemProps {
	name: string;
	isComplete: boolean;
	isLast?: boolean;
	args?: Record<string, unknown>;
	humanReadable?: string;
	toolOutput?: string;
	integrationId?: string;
}

function formatArgValue(value: unknown): string {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (Array.isArray(value)) {
		return value.map((v) => formatArgValue(v)).join(', ');
	}
	if (value && typeof value === 'object') {
		return JSON.stringify(value);
	}
	return '';
}

interface FormattedSqlProps {
	query: string;
	classes: any;
}

function FormattedSql({ query, classes }: FormattedSqlProps) {
	const formattedQuery = useFmtSql(query, 'sql');

	return (
		<Prism
			language="sql"
			trim
			classNames={{
				root: classes.sqlEditor,
				code: classes.sqlCode,
			}}
		>
			{formattedQuery ?? query}
		</Prism>
	);
}

// Get integration type from API
async function getIntegrationType(
	integrationId: string
): Promise<string | null> {
	try {
		const response = await apiClient.get(
			`/integration/integrations/${integrationId}/`
		);
		return (response.data as { type?: string })?.type ?? null;
	} catch {
		return null;
	}
}

// Handle OAuth connect button click
async function handleOAuthConnect(integrationId: string) {
	try {
		const integrationType = await getIntegrationType(integrationId);
		if (!integrationType) {
			throw new Error('Could not determine integration type');
		}
		await initiateIntegrationOauth(integrationId, integrationType, {
			openInNewTab: true,
			returnUrl: '/settings/connected-accounts',
		});
		showNotification({
			title: 'Authentication in progress',
			message:
				'Please complete the authentication in the new tab. Once complete, try running the query again.',
			color: 'blue',
			autoClose: 10000,
		});
	} catch (error) {
		showNotification({
			title: 'Authentication failed',
			message:
				(error as Error).message ||
				'Failed to initiate OAuth authentication. Please try again.',
			color: 'red',
		});
	}
}

// Show OAuth notification with Connect button
function showOAuthNotification(integrationId: string) {
	showNotification({
		title: 'Authentication required',
		message: (
			<Stack spacing="xs">
				<Text size="sm">Connect your account to run queries in Secoda</Text>
				<Button
					onClick={() => handleOAuthConnect(integrationId)}
					variant="default"
					size="sm"
					rightIconName="arrowUpRight"
				>
					Connect
				</Button>
			</Stack>
		),
		color: 'blue',
		autoClose: false,
	});
}

// Check OAuth status for an integration
async function checkOAuthStatus(integrationId: string): Promise<boolean> {
	try {
		const integrationType = await getIntegrationType(integrationId);
		if (!integrationType) {
			return false;
		}
		const response = await fetch(
			`/api/integration_v2/integrations/${integrationId}/${integrationType}/oauth/status/`,
			{ credentials: 'include' }
		);
		if (response.ok) {
			const data = await response.json();
			return data.connected === true;
		}
		return false;
	} catch {
		return false;
	}
}

function ToolTimelineItem({
	name,
	args,
	humanReadable,
	isComplete,
	toolOutput,
	integrationId,
}: ToolTimelineItemProps) {
	const { classes } = useStyles();
	const oauthNotificationShownRef = useRef(false);
	const oauthStatusCheckedRef = useRef(false);

	const isOAuthError = toolOutput && detectOAuthError(toolOutput).hasOAuthError;

	// Show OAuth notification when OAuth error is detected and completion status changes, but only if not already connected
	useDidUpdate(() => {
		if (
			isOAuthError &&
			integrationId &&
			isComplete &&
			!oauthNotificationShownRef.current &&
			!oauthStatusCheckedRef.current
		) {
			oauthStatusCheckedRef.current = true;
			// Check if user is already connected before showing notification
			checkOAuthStatus(integrationId).then((isConnected) => {
				if (!isConnected) {
					oauthNotificationShownRef.current = true;
					showOAuthNotification(integrationId);
				}
			});
		}
	}, [isOAuthError, integrationId, isComplete]);

	if (!humanReadable) {
		return null;
	}

	// Handle different tool types with specific formatting
	const renderToolContent = () => {
		if (!args) return null;
		const validation = validateToolArgs(name, args);

		// If validation fails, show error and fallback to generic display
		if (!validation.isValid) {
			return (
				<Text size="sm">
					<span className={classes.boldText}>Raw args:</span>{' '}
					{JSON.stringify(args, null, 2)}
				</Text>
			);
		}

		const validatedArgs = validation.validated!;

		switch (name) {
			case 'run_sql':
				const runSqlArgs = validatedArgs as RunSqlArgs;
				return (
					<Stack spacing="xs">
						<FormattedSql query={runSqlArgs.query} classes={classes} />
						{runSqlArgs.integration_id && (
							<Text size="sm" color="text/secondary/default">
								<span className={classes.boldText}>Integration ID:</span>{' '}
								{runSqlArgs.integration_id}
							</Text>
						)}
					</Stack>
				);

			case 'execute_sql':
				const executeSqlArgs = validatedArgs as ExecuteSqlArgs;
				return (
					<Stack spacing="xs">
						<Stack spacing="xs">
							{executeSqlArgs.queries.map((query: string, index: number) => (
								<FormattedSql key={index} query={query} classes={classes} />
							))}
						</Stack>
						{executeSqlArgs.integration_id && (
							<Text size="sm" color="text/secondary/default">
								<span className={classes.boldText}>Integration ID:</span>{' '}
								{executeSqlArgs.integration_id}
							</Text>
						)}
					</Stack>
				);

			case 'create_todos':
				const createTodosArgs = validatedArgs as CreateTodosArgs;
				const todosText = createTodosArgs.todos;
				const todoLines = todosText.split('\n').filter((line) => line.trim());

				// Extract todo items and non-todo lines
				const todoItems: string[] = [];
				const nonTodoLines: string[] = [];

				todoLines.forEach((line) => {
					const trimmedLine = line.trim();
					const isCheckbox =
						trimmedLine.startsWith('- [ ]') ||
						trimmedLine.startsWith('* [ ]') ||
						trimmedLine.startsWith('[ ]');

					if (isCheckbox) {
						// Extract the todo text after the checkbox
						const todoText = trimmedLine
							.replace(/^[-*]\s*\[\s*\]\s*/, '')
							.replace(/^\[\s*\]\s*/, '');
						todoItems.push(todoText);
					} else {
						nonTodoLines.push(trimmedLine);
					}
				});

				return (
					<Stack spacing="xs">
						{nonTodoLines.map((line, index) => (
							<Text
								key={`non-todo-${index}`}
								size="sm"
								color="text/secondary/default"
								className={classes.textLineHeight}
							>
								{line}
							</Text>
						))}
						{todoItems.length > 0 && (
							<List>
								{todoItems.map((todoText, index) => (
									<List.Item key={index}>
										<Text
											size="sm"
											color="text/primary/default"
											className={classes.textLineHeight}
										>
											{todoText}
										</Text>
									</List.Item>
								))}
							</List>
						)}
					</Stack>
				);

			// TODO: maybe display a table? the raw output might be hard to parse
			// case 'submit_relevant_table_columns':
			// 	if (args.output) {
			// 		return (
			// 			<Stack spacing="xs">
			// 				<Text size="xs" weight="semibold" color="text/secondary/default">
			// 					Table Schema:
			// 				</Text>
			// 				<Box
			// 					component="pre"
			// 					style={{
			// 						fontSize: '12px',
			// 						fontFamily: 'monospace',
			// 						whiteSpace: 'pre-wrap',
			// 						backgroundColor: 'var(--mantine-color-gray-0)',
			// 						padding: '8px',
			// 						borderRadius: '4px',
			// 						border: '1px solid var(--mantine-color-gray-2)',
			// 						maxHeight: '200px',
			// 						overflow: 'auto',
			// 					}}
			// 				>
			// 					{formatArgValue(args.output)}
			// 				</Box>
			// 			</Stack>
			// 		);
			// 	}
			// 	break;

			case 'document_findings':
				const documentFindingsArgs = validatedArgs as DocumentFindingsArgs;
				return (
					<Stack spacing="xs">
						<Box component="pre" className={classes.preElement}>
							{documentFindingsArgs.findings}
						</Box>
					</Stack>
				);

			case 'trigger_agent':
				const triggerAgentArgs = validatedArgs as TriggerAgentArgs;
				return (
					<Stack spacing="xs">
						<Text size="sm" color="text/secondary/default">
							<span className={classes.boldText}>Agent:</span>{' '}
							{triggerAgentArgs.agent_name}
						</Text>
						<Box component="pre" className={classes.preElement}>
							{triggerAgentArgs.prompt}
						</Box>
					</Stack>
				);

			case 'createMetrics':
			case 'createDashboard':
				const createArgs = validatedArgs as
					| CreateMetricsArgs
					| CreateDashboardArgs;
				return (
					<Stack spacing="xs">
						{Object.entries(createArgs).map(([key, value]) => (
							<Text key={key} size="sm" color="text/secondary/default">
								<span className={classes.boldText}>{key}:</span>{' '}
								{key === 'bi_metrics' || key === 'bi_metric_ids' ? (
									<span className={classes.successText}>
										{Array.isArray(value) ? `${value.length} items` : 'Created'}
									</span>
								) : (
									formatArgValue(value)
								)}
							</Text>
						))}
					</Stack>
				);

			default:
				// Generic handling for other tools
				return null;
		}
	};

	let title = humanReadable;
	let content = null;
	if (humanReadable.startsWith('Taking note:')) {
		title = 'Taking note';
		content = humanReadable.split('Taking note:')?.[1] || null;
	}

	const toolContent = renderToolContent();

	// If there's no tool content and no special content, don't render expandable block
	if (!toolContent && !content) {
		return (
			<ReasoningCollapse
				title={title}
				icon={TOOLS_ICONS?.[name]}
				isLoading={!isComplete}
			/>
		);
	}

	return (
		<ReasoningCollapse
			title={title}
			icon={TOOLS_ICONS?.[name]}
			isLoading={!isComplete}
		>
			<Stack
				className={TOOLS_ICONS?.[name] ? classes.indentedContent : undefined}
			>
				{content && (
					<Text size="sm" color="text/secondary/default">
						{content}
					</Text>
				)}
				{toolContent}
			</Stack>
		</ReasoningCollapse>
	);
}

interface MessageGroup {
	group: string | null;
	messages: Array<{ message: Message; index: number }>;
}

interface ThinkingSectionProps {
	messages: Message[];
	promptRunning: boolean;
	uniqueId: string;
}

export function ThinkingSection({
	messages,
	promptRunning,
	uniqueId,
}: ThinkingSectionProps) {
	const { classes } = useStyles();
	const [opened, { toggle }] = useDisclosure(false);

	const thinkingTitle = useMemo(() => {
		if (messages.length === 0) {
			return 'Thinking';
		}

		const latestMessage = messages[messages.length - 1];
		return (
			latestMessage.tool_response?.human_readable ??
			latestMessage.use_tools?.[0]?.name ??
			'Thinking'
		);
	}, [messages]);

	// Group messages by their group attribute
	const messageGroups = useMemo(() => {
		const groups: MessageGroup[] = [];
		let currentGroup: MessageGroup | null = null;

		messages
			.filter((message) => message.use_tools?.[0]?.name !== 'trigger_agent')
			.forEach((message, index) => {
				const messageGroup = message.group ?? null;

				if (currentGroup && currentGroup.group === messageGroup) {
					// Add to existing group
					currentGroup.messages.push({ message, index });
				} else {
					// Start new group
					currentGroup = {
						group: messageGroup,
						messages: [{ message, index }],
					};
					groups.push(currentGroup);
				}
			});

		return groups;
	}, [messages]);

	const renderMessage = (msg: Message, index: number, isLast: boolean) => {
		if (msg.content !== '' && msg.use_tools?.length === 0) {
			return (
				<AnimatedContent
					cacheKey={uniqueId + index}
					content={msg.content}
					isTyping={promptRunning}
					key={msg.content + msg.sender}
					isReasoning
				/>
			);
		} else if (msg.use_tools?.length > 0) {
			// Extract integration ID from tool args for OAuth flow
			const toolArgs = msg.use_tools[0].args as Record<string, unknown>;
			const toolIntegrationId = toolArgs?.integration_id as string | undefined;

			return (
				<ToolTimelineItem
					key={msg.use_tools[0].name + index}
					name={msg.use_tools[0].name}
					args={msg.use_tools[0].args}
					humanReadable={
						msg.tool_response?.human_readable ??
						msg.use_tools[0].name ??
						undefined
					}
					toolOutput={msg.tool_response?.output}
					integrationId={toolIntegrationId}
					isComplete={index !== messages.length - 1 || !promptRunning}
					isLast={isLast}
				/>
			);
		}
		return null;
	};

	return (
		<Stack spacing={0}>
			<UnstyledButton
				onClick={toggle}
				mb="sm"
				data-active={opened ? 'true' : undefined}
				className={classes.toggleButton}
			>
				<Group spacing="4xs" noWrap>
					{promptRunning ? (
						<ThinkingText size="md" className={classes.thinkingText} truncate>
							{thinkingTitle.endsWith('.')
								? thinkingTitle.substring(0, thinkingTitle.length - 1)
								: thinkingTitle}
						</ThinkingText>
					) : (
						<Text size="md" className={classes.thinkingText} truncate>
							Reasoning
						</Text>
					)}
					<IconChevronDown
						className={classes.chevron}
						data-expanded={opened ? 'true' : undefined}
						size={20}
					/>
				</Group>
			</UnstyledButton>
			<Collapse
				in={opened}
				mb={opened ? 'sm' : undefined}
				transitionDuration={200}
				transitionTimingFunction="ease"
				animateOpacity
			>
				<Stack
					className={classes.expandContent}
					spacing={0}
					p="sm"
					pl={0}
					id="thinking-section-content"
				>
					{messages.length === 0 && promptRunning ? (
						// Show skeleton when no messages and prompt is running
						<Stack spacing="md" p="sm">
							<TextSkeleton size="sm" width="60%" />
							<TextSkeleton size="sm" width="80%" />
							<TextSkeleton size="sm" width="45%" />
						</Stack>
					) : (
						messageGroups.map((group, groupIndex) => {
							const isLastGroup = groupIndex === messageGroups.length - 1;

							if (group.group) {
								// Render grouped messages with header

								const groupName =
									group.group.split(':')?.[0]?.trim() ?? group.group;
								const groupLabel = TOOLS_GROUP_LABELS?.[groupName] ?? groupName;

								return (
									<Box key={`group-${group.group}-${groupIndex}`}>
										<ReasoningCollapse
											title={groupLabel}
											icon={TOOLS_ICONS?.[groupName] || 'brain'}
										>
											<Box>
												{group.messages.map(({ message, index }, msgIndex) => {
													const isLastInGroup =
														msgIndex === group.messages.length - 1;
													const isLastOverall = isLastGroup && isLastInGroup;
													return renderMessage(message, index, isLastOverall);
												})}
											</Box>
										</ReasoningCollapse>
									</Box>
								);
							} else {
								// Render ungrouped messages normally
								return group.messages.map(({ message, index }, msgIndex) => {
									const isLastInGroup = msgIndex === group.messages.length - 1;
									const isLastOverall = isLastGroup && isLastInGroup;
									return renderMessage(message, index, isLastOverall);
								});
							}
						})
					)}
				</Stack>
			</Collapse>
			<Box id={uniqueId} />
		</Stack>
	);
}
